import i18n from '@/lib/i18n';
import { authService } from '@/service/AuthService';
import { useSettingsStore } from '@/store/SettingsStore.ts';
import { UserSettings } from 'intermind';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 1000;

export const useUserSettingsSync = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const initializedRef = useRef(false);
  const debounceTimerRef = useRef<number | null>(null);

  // Load user settings from the server
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    (async () => {
      setIsSyncing(true);
      try {
        await authService.restoreServerUserSettings();

        // Set the store as initialized to start saving settings to server on their change
        useSettingsStore.getState().setInitialized(true);
      } catch (error) {
        console.error('Failed to load user settings:', error);
        toast.error(i18n.t('errors.loadUserSettingsFailed'));
      } finally {
        setIsSyncing(false);
      }
    })();
  }, []);

  // Save user settings to the server on store change
  useEffect(() => {
    const unsubscribe = useSettingsStore.subscribe((state) => {
      if (state.isInitialized) {
        // Clear any existing timer
        if (debounceTimerRef.current) {
          window.clearTimeout(debounceTimerRef.current);
        }

        // Set a new timer
        debounceTimerRef.current = window.setTimeout(() => {
          setIsSyncing(true);
          authService
            .updateUserInfo({
              settings: state as unknown as UserSettings,
            })
            .finally(() => {
              setIsSyncing(false);
            });
        }, DEBOUNCE_DELAY);

        if (state.language && state.language !== i18n.language) {
          i18n.changeLanguage(state.language);
        }
      }
    });

    return () => {
      unsubscribe?.();
      // Clear any pending timer on cleanup
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { isSyncing };
};

// For backward compatibility
const UserSettingsSync = () => {
  useUserSettingsSync();
  return null;
};

export default UserSettingsSync;
